
import { SVG, Svg, Image, ClipPath, Box, Element as SvgElement, G
       } from '@svgdotjs/svg.js';
import { GalleryImage } from 'app/gallery/gallery.service';
import { GalleryConstants, StageConstants } from 'core/constants';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';

interface ImageEnvironmentObjects
{
  img: Image;
  wrapper: SvgElement;
  clipper: ClipPath;
}

function getImageEnvironment(img: Image): ImageEnvironmentObjects
{
  const wrapper = (<any>img).parent() as SvgElement;
  const wrapperParent = wrapper.parent() as G;

  return {
    img: img,
    wrapper: wrapper,
    clipper: wrapper && StageUtils.findClipPath(wrapperParent),
  };
}

function determineTargetMaxSize(img: Image): number
{
  const toMaxSize = (box: Box) => {
    if (box.width > box.height) {
      return Math.round(box.width);
    }
    else {
      return Math.round(box.height);
    }
  };

  const isValidBox = (box: Box): boolean =>
    box.width !== 0 && box.height !== 0;

  const withoutUndefined = (els: Array<any>): Array<any> =>
    els.filter(el => !!el);

  const env = getImageEnvironment(img);

  const clipShapes = env.clipper ? env.clipper.children() : [];

  let elements = [env.img, ...clipShapes, env.wrapper];

  if (img.id().includes('use')) {
    const reference = '#' + (<any>img.parent()).id();
    elements = elements.concat(
        (<any>img.root().find('use'))
          .filter(e => e.attr('xlink:href') === reference)
          .map(e => e.parent()));
  }

  const boxDims = withoutUndefined(withoutUndefined(elements)
    .map(el => el as SvgElement)
    .map(el => el.bbox())
    .filter(isValidBox)
    .map(toMaxSize)
    .map((s) => s * GalleryConstants.MULTI_VERSION_IMAGE_RESOLUTION_MULTIPLIER)
    .concat([GalleryConstants.THUMB_HEIGHT]));
  boxDims.sort((a, b) => b - a);

  const parentRect = img.node.closest('div').getBoundingClientRect();
  const maxDims = (parentRect.width > parentRect.height ?
                   parentRect.width : parentRect.height) *
                  GalleryConstants.MULTI_VERSION_IMAGE_RESOLUTION_MULTIPLIER;

  return Utils.steppedLimit(Math.ceil(Math.min(boxDims.shift(), maxDims)));
}

function determineBaseUrl(url: string): string
{
  return url.split('?')[0];
}

function determineMultiversionUrl(
  img: Image, baseUrl: string): string
{
  return baseUrl + '?' +
      `limit={"short_side": ${determineTargetMaxSize(img)}}`;
}

function multiversionImageLoad(url?: string): string | undefined
{
  const requestedUrl =
      url || this.node.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

  if (!requestedUrl || requestedUrl === '' ||
      requestedUrl === Utils.url(StageConstants.PIXEL_WHITE)) {
    return url;
  }

  const baseUrl = determineBaseUrl(requestedUrl);

  this.attr('pap:href', baseUrl);

  const finalUrl = (
      StageUtils.isPlaceholder(this.node) ||
          StageUtils.isAsset(this.node.parentNode)
          ? url
          : determineMultiversionUrl(this, baseUrl));

  if (!this.loaded) {
    this.loaded = () => {};
  }

  return Image.prototype.load.bind(this)(finalUrl, this.loaded);
}

function delayedFunction(fn: () => any, timeout: number): () => any
{
  let timeoutHandle = undefined;

  return () => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }

    timeoutHandle = setTimeout(() => fn(), timeout);
  };
}

function attachResizeListener(img: Image, timeout: number): Image
{
  const resizeHandler = delayedFunction(() => {
    if (! document.body.contains(img.node)) {
      window.removeEventListener('resize', resizeHandler);
      return;
    }

    img.load();
  }, timeout);

  window.addEventListener('resize', resizeHandler);

  return img;
}

export function loadUrl(img: Image, url: string): string
{
  return multiversionImageLoad.bind(img)(url);
}

export function injectMultiversionImageBehavior(img: any): Image
{
  const RELOAD_TIMEOUT = 800;

  img.load = multiversionImageLoad.bind(img);
  img.scheduleReload = delayedFunction(img.load, RELOAD_TIMEOUT);
  attachResizeListener(img, RELOAD_TIMEOUT);

  return img as Image;
}

export function findMultiversionImageCandidates(svgDoc: any): any
{
  return StageUtils.getAllWrappedImages(SVG(svgDoc.firstChild as SVGSVGElement))
    .map((wrapped) => wrapped.image.node);
}
